/**
 * Transaction Builder Component
 * Advanced transaction builder with security search and real-time pricing
 */

import React, { useState, useEffect } from 'react';
import { Search, X, TrendingUp, TrendingDown, DollarSign, Loader } from 'lucide-react';
import { httpClient } from '../../lib/httpClient';
import { toast } from 'sonner';

interface Security {
  symbol: string;
  name: string;
  type: string;
  exchange: string;
  currency: string;
  price?: number;
  change?: number;
  change_percent?: number;
}

interface Transaction {
  type: 'buy' | 'sell' | 'cash';
  symbol?: string;
  name?: string;
  quantity?: number;
  price?: number;
  currency?: string;
  amount?: number;
  source?: string; // Source or comment for cash transactions
}

interface TransactionBuilderProps {
  onAdd: (transaction: Transaction) => void;
  onClose: () => void;
  currentPositions?: Array<{ 
    symbol: string; 
    quantity: number;
    name?: string;
    price?: number;
  }>;
}

// ISIN to Symbol mapping for common stocks
const ISIN_TO_SYMBOL: Record<string, string> = {
  // Swiss stocks
  'CH0012005267': 'NOVN',    // Novartis
  'CH0012032048': 'ROG',     // Roche
  'CH0038863350': 'NESN',    // Nestlé
  'CH0012221716': 'ABBN',    // ABB
  'CH0244767585': 'UBSG',    // UBS Group
  'CH0011075394': 'ZURN',    // Zurich Insurance
  'CH0010645932': 'GIVN',    // Givaudan
  'CH0012138530': 'CFR',     // Compagnie Financière Richemont
  'CH0210483332': 'CSGN',    // Credit Suisse
  'CH0126881561': 'SLHN',    // Swiss Life Holding
  // US stocks
  'US0378331005': 'AAPL',    // Apple
  'US5949181045': 'MSFT',    // Microsoft
  'US02079K3059': 'GOOGL',   // Alphabet
  'US0231351067': 'AMZN',    // Amazon
  'US88160R1014': 'TSLA',    // Tesla
  'US30303M1027': 'META',    // Meta
  'US67066G1040': 'NVDA',    // NVIDIA
};

const TransactionBuilder: React.FC<TransactionBuilderProps> = ({ 
  onAdd, 
  onClose, 
  currentPositions = [] 
}) => {
  const [type, setType] = useState<'buy' | 'sell' | 'cash'>('buy');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSecurity, setSelectedSecurity] = useState<Security | null>(null);
  const [securities, setSecurities] = useState<Security[]>([]);
  const [searchResults, setSearchResults] = useState<Security[]>([]);
  const [quantity, setQuantity] = useState<number>(0);
  const [price, setPrice] = useState<number>(0);
  const [currency, setCurrency] = useState('USD');
  const [amount, setAmount] = useState<number>(0);
  const [source, setSource] = useState<string>(''); // Source/comment for cash
  const [loading, _setLoading] = useState(false);
  const [loadingPrice, setLoadingPrice] = useState(false);
  const [loadingSecurities, setLoadingSecurities] = useState(false);
  const [showSearch, setShowSearch] = useState(false);

  // Load available securities on mount
  useEffect(() => {
    loadSecurities();
  }, []);

  // Update price when security is selected
  useEffect(() => {
    if (selectedSecurity) {
      setPrice(selectedSecurity.price || 0);
      setCurrency(selectedSecurity.currency);
      if (selectedSecurity.price) {
        fetchLatestPrice(selectedSecurity.symbol);
      }
    }
  }, [selectedSecurity]);

  // Filter search results
  useEffect(() => {
    if (!selectedSecurity) {
      if (type === 'sell') {
        // In sell mode, only show positions from portfolio
        if (searchQuery.length > 0) {
          // Filter portfolio positions by search
          const filtered = currentPositions.filter(p => {
            if (p.quantity <= 0) return false;
            // Convert ISIN to symbol if needed
            const symbol = ISIN_TO_SYMBOL[p.symbol] || p.symbol;
            const name = p.name || symbol;
            return (
              symbol.toLowerCase().includes(searchQuery.toLowerCase()) ||
              name.toLowerCase().includes(searchQuery.toLowerCase())
            );
          });
          // Convert to security format for display with proper symbols
          setSearchResults(filtered.map(p => ({
            symbol: ISIN_TO_SYMBOL[p.symbol] || p.symbol,
            name: p.name || ISIN_TO_SYMBOL[p.symbol] || p.symbol,
            type: 'Stock',
            exchange: '',
            currency: 'CHF',
            price: p.price || 0
          })));
        } else if (showSearch) {
          // Show all portfolio positions when focused
          const portfolioSecurities = currentPositions
            .filter(p => p.quantity > 0)
            .map(p => ({
              symbol: ISIN_TO_SYMBOL[p.symbol] || p.symbol,
              name: p.name || ISIN_TO_SYMBOL[p.symbol] || p.symbol,
              type: 'Stock',
              exchange: '',
              currency: 'CHF',
              price: p.price || 0
            }));
          setSearchResults(portfolioSecurities);
        } else {
          setSearchResults([]);
        }
      } else {
        // Buy mode - show all securities
        if (searchQuery.length > 0) {
          const filtered = securities.filter(s => 
            (s.symbol && s.symbol.toLowerCase().includes(searchQuery.toLowerCase())) ||
            (s.name && s.name.toLowerCase().includes(searchQuery.toLowerCase()))
          );
          setSearchResults(filtered.slice(0, 10));
        } else if (showSearch && securities.length > 0) {
          setSearchResults(securities.slice(0, 10));
        } else {
          setSearchResults([]);
        }
      }
    } else {
      setSearchResults([]);
    }
  }, [searchQuery, securities, selectedSecurity, showSearch, type, currentPositions]);

  const loadSecurities = async () => {
    if (loadingSecurities) return;
    
    // Check cache first
    const cachedSecurities = sessionStorage.getItem('available_securities');
    if (cachedSecurities) {
      const cached = JSON.parse(cachedSecurities);
      // Use cache if less than 5 minutes old
      if (cached.timestamp && Date.now() - cached.timestamp < 5 * 60 * 1000) {
        setSecurities(cached.data);
        return;
      }
    }
    
    setLoadingSecurities(true);
    try {
      // Use skip_prices=true for fast loading
      const response = await httpClient.get<any>(
        '/api/market/securities/available?skip_prices=true'
      );
      const securities = response.securities || [];
      setSecurities(securities);
      
      // Cache the results
      sessionStorage.setItem('available_securities', JSON.stringify({
        data: securities,
        timestamp: Date.now()
      }));
    } catch (error) {
      console.error('Failed to load securities:', error);
      // Fallback to hardcoded securities if API fails
      const fallbackSecurities = [
        { symbol: 'AAPL', name: 'Apple Inc', type: 'Stock', exchange: 'NASDAQ', currency: 'USD' },
        { symbol: 'MSFT', name: 'Microsoft Corp', type: 'Stock', exchange: 'NASDAQ', currency: 'USD' },
        { symbol: 'GOOGL', name: 'Alphabet Inc', type: 'Stock', exchange: 'NASDAQ', currency: 'USD' },
        { symbol: 'NESN', name: 'Nestlé SA', type: 'Stock', exchange: 'SIX', currency: 'CHF' },
        { symbol: 'NOVN', name: 'Novartis AG', type: 'Stock', exchange: 'SIX', currency: 'CHF' },
        { symbol: 'ABBN', name: 'ABB Ltd', type: 'Stock', exchange: 'SIX', currency: 'CHF' },
        { symbol: 'UBSG', name: 'UBS Group AG', type: 'Stock', exchange: 'SIX', currency: 'CHF' },
        { symbol: 'ZURN', name: 'Zurich Insurance', type: 'Stock', exchange: 'SIX', currency: 'CHF' },
      ];
      setSecurities(fallbackSecurities);
    } finally {
      setLoadingSecurities(false);
    }
  };

  const fetchLatestPrice = async (symbol: string) => {
    setLoadingPrice(true);
    try {
      const response = await httpClient.get<any>(`/api/market/quote/${symbol}`);
      if (response && response.price) {
        setPrice(response.price);
        toast.success(`Price updated: ${response.price} ${currency}`);
      } else {
        toast.warning('Price not available');
      }
    } catch (error) {
      console.error('Failed to fetch price:', error);
      toast.warning('Failed to fetch latest price. Using manual entry.');
    } finally {
      setLoadingPrice(false);
    }
  };

  const calculateTotal = () => {
    if (type === 'cash') return amount;
    return quantity * price;
  };

  const handleSubmit = () => {
    if (type === 'cash') {
      if (amount <= 0) {
        toast.warning('Please enter a valid amount');
        return;
      }
      if (!source || source.trim() === '') {
        toast.warning('Please enter the source of the cash');
        return;
      }
      onAdd({ type: 'cash', amount, currency, source: source.trim() });
    } else {
      // Check if we have a selected security or manual entry
      const hasSymbol = selectedSecurity?.symbol || searchQuery.trim();
      
      if (!hasSymbol) {
        toast.warning('Please select or enter a security symbol');
        return;
      }
      
      if (quantity <= 0) {
        toast.warning('Please enter a valid quantity');
        return;
      }
      
      if (price <= 0) {
        toast.warning('Please enter a valid price');
        return;
      }
      
      onAdd({ 
        type, 
        symbol: selectedSecurity?.symbol || searchQuery.trim().toUpperCase(),
        name: selectedSecurity?.name || searchQuery.trim(),
        quantity, 
        price, 
        currency: selectedSecurity?.currency || currency
      });
    }
    onClose();
  };

  const selectSecurity = (security: Security) => {
    setSelectedSecurity(security);
    setSearchQuery(security.symbol);
    setShowSearch(false);
    // Set currency from selected security
    if (security.currency) {
      setCurrency(security.currency);
    }
  };

  return (
    <div className="fixed inset-0 z-60 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg p-6 w-[500px] max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold">Add Transaction</h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="space-y-5">
          {/* Transaction Type */}
          <div>
            <label className="block text-sm font-medium mb-2">Transaction Type</label>
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => setType('buy')}
                className={`px-4 py-2 rounded-lg border-2 transition-all ${
                  type === 'buy' 
                    ? 'border-green-500 bg-green-50 text-green-700' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <TrendingUp className="w-4 h-4 inline mr-1" />
                Buy
              </button>
              <button
                onClick={() => setType('sell')}
                className={`px-4 py-2 rounded-lg border-2 transition-all ${
                  type === 'sell' 
                    ? 'border-red-500 bg-red-50 text-red-700' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <TrendingDown className="w-4 h-4 inline mr-1" />
                Sell
              </button>
              <button
                onClick={() => setType('cash')}
                className={`px-4 py-2 rounded-lg border-2 transition-all ${
                  type === 'cash' 
                    ? 'border-blue-500 bg-blue-50 text-blue-700' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <DollarSign className="w-4 h-4 inline mr-1" />
                Cash
              </button>
            </div>
          </div>

          {type !== 'cash' ? (
            <>
              {/* Security Search */}
              <div>
                <label className="block text-sm font-medium mb-2">Security</label>
                <div className="relative">
                  <div className="relative">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setSelectedSecurity(null);
                        setShowSearch(true);
                      }}
                      onFocus={() => {
                        setShowSearch(true);
                        if (securities.length === 0) {
                          loadSecurities();
                        }
                      }}
                      onBlur={() => {
                        // Delay hiding to allow click on dropdown items
                        setTimeout(() => setShowSearch(false), 200);
                      }}
                      placeholder="Search by symbol or name..."
                      className="w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                    <Search className="absolute right-3 top-2.5 w-5 h-5 text-gray-400" />
                  </div>

                  {/* Search Results Dropdown */}
                  {showSearch && (
                    <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {loadingSecurities ? (
                        <div className="px-4 py-3 text-center text-gray-500">
                          <Loader className="w-5 h-5 animate-spin inline mr-2" />
                          Loading securities...
                        </div>
                      ) : searchResults.length > 0 ? (
                        searchResults.map((security) => (
                          <button
                            key={security.symbol}
                            onClick={() => selectSecurity(security)}
                            className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b last:border-b-0"
                            disabled={false}
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-semibold">{security.symbol}</p>
                                <p className="text-sm text-gray-600">{security.name}</p>
                                <p className="text-xs text-gray-500">
                                  {security.type} • {security.exchange}
                                </p>
                              </div>
                              <div className="text-right">
                                {security.price && (
                                  <>
                                    <p className="font-medium">
                                      {security.price.toFixed(2)} {security.currency}
                                    </p>
                                    {security.change_percent && (
                                      <p className={`text-sm ${
                                        security.change_percent > 0 ? 'text-green-600' : 'text-red-600'
                                      }`}>
                                        {security.change_percent > 0 ? '+' : ''}
                                        {security.change_percent.toFixed(2)}%
                                      </p>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          </button>
                        ))
                      ) : (
                        <div className="px-4 py-3 text-center text-gray-500">
                          {searchQuery ? 'No matching securities found' : 'Start typing to search...'}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Selected Security Display */}
                {selectedSecurity && (
                  <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                    <p className="font-semibold">{selectedSecurity.symbol}</p>
                    <p className="text-sm text-gray-600">{selectedSecurity.name}</p>
                  </div>
                )}
              </div>

              {/* Quantity */}
              <div>
                <label className="block text-sm font-medium mb-2">Quantity</label>
                <input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(0, parseInt(e.target.value) || 0))}
                  onFocus={(e) => {
                    // Clear the field if it's 0 when focused
                    if (quantity === 0) {
                      setQuantity('' as any);
                      e.target.value = '';
                    }
                  }}
                  onBlur={(e) => {
                    // Set back to 0 if left empty
                    if (e.target.value === '') {
                      setQuantity(0);
                    }
                  }}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  min="1"
                  step="1"
                  placeholder="Enter quantity"
                />
                {type === 'sell' && selectedSecurity && (
                  <p className="text-sm text-gray-500 mt-1">
                    Available: {currentPositions.find(p => {
                      const positionSymbol = ISIN_TO_SYMBOL[p.symbol] || p.symbol;
                      return positionSymbol === selectedSecurity.symbol;
                    })?.quantity || 0} shares
                  </p>
                )}
              </div>

              {/* Price */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Price per Share
                  {loadingPrice && <Loader className="inline ml-2 w-3 h-3 animate-spin" />}
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    value={price}
                    onChange={(e) => setPrice(Math.max(0, parseFloat(e.target.value) || 0))}
                    onFocus={(e) => {
                      // Clear the field if it's 0 when focused
                      if (price === 0) {
                        setPrice('' as any);
                        e.target.value = '';
                      }
                    }}
                    onBlur={(e) => {
                      // Set back to 0 if left empty
                      if (e.target.value === '') {
                        setPrice(0);
                      }
                    }}
                    className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    step="0.01"
                    min="0.01"
                    placeholder="Enter price"
                  />
                  <select
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                    className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="CHF">CHF</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                  </select>
                </div>
                {selectedSecurity && (
                  <button
                    onClick={() => fetchLatestPrice(selectedSecurity.symbol)}
                    disabled={loadingPrice}
                    className={`text-sm mt-1 flex items-center gap-1 ${
                      loadingPrice 
                        ? 'text-gray-400 cursor-not-allowed' 
                        : 'text-blue-600 hover:text-blue-700 cursor-pointer'
                    }`}
                  >
                    {loadingPrice ? (
                      <>
                        <Loader className="w-3 h-3 animate-spin" />
                        <span>Fetching price...</span>
                      </>
                    ) : (
                      'Refresh latest price'
                    )}
                  </button>
                )}
              </div>

              {/* Total */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Total Amount</span>
                  <span className="text-xl font-bold">
                    {calculateTotal().toFixed(2)} {currency}
                  </span>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Cash Amount */}
              <div>
                <label className="block text-sm font-medium mb-2">Amount to Add</label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(Math.max(0, parseFloat(e.target.value) || 0))}
                    onFocus={(e) => {
                      // Clear the field if it's 0 when focused
                      if (amount === 0) {
                        setAmount('' as any);
                        e.target.value = '';
                      }
                    }}
                    onBlur={(e) => {
                      // Set back to 0 if left empty
                      if (e.target.value === '') {
                        setAmount(0);
                      }
                    }}
                    className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    step="100"
                    min="0"
                    placeholder="Enter amount"
                  />
                  <select
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                    className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="CHF">CHF</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                  </select>
                </div>
              </div>

              {/* Source/Comment */}
              <div>
                <label className="block text-sm font-medium mb-2">Source / Comment</label>
                <select
                  value={source}
                  onChange={(e) => setSource(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select source...</option>
                  <option value="Client deposit">Client deposit</option>
                  <option value="Dividend payment">Dividend payment</option>
                  <option value="Interest payment">Interest payment</option>
                  <option value="Bond maturity">Bond maturity</option>
                  <option value="Coupon payment">Coupon payment</option>
                  <option value="Sale proceeds">Sale proceeds</option>
                  <option value="Loan proceeds">Loan proceeds</option>
                  <option value="Inheritance">Inheritance</option>
                  <option value="Gift">Gift</option>
                  <option value="Other">Other</option>
                </select>
                {source === 'Other' && (
                  <input
                    type="text"
                    placeholder="Please specify..."
                    onChange={(e) => setSource(`Other: ${e.target.value}`)}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 mt-2"
                  />
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Required for compliance and audit purposes
                </p>
              </div>
            </>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 mt-6 pt-6 border-t">
          <button
            onClick={onClose}
            className="px-6 py-2 text-gray-600 hover:text-gray-900"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className={`px-6 py-2 rounded-lg text-white font-medium ${
              type === 'buy' 
                ? 'bg-green-600 hover:bg-green-700' 
                : type === 'sell'
                ? 'bg-red-600 hover:bg-red-700'
                : 'bg-blue-600 hover:bg-blue-700'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {loading ? (
              <Loader className="w-5 h-5 animate-spin" />
            ) : (
              `Add ${type === 'cash' ? 'Cash' : type === 'buy' ? 'Buy Order' : 'Sell Order'}`
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransactionBuilder;